import { useMutation, useQuery } from "@tanstack/react-query";
import { Input, InputNumber, Select } from "antd";
import {
  StyledButton,
  StyledInput
} from "common/components/StyledElements";
import APIConstants from "common/constants/APIConstants";
import { useAuthContext } from "common/contexts/AuthContext";
import backArrow from "common/icons/backArrow.svg";
import { api } from "common/utils/APIMethods";
import { MESSAGE_TYPE, showMessage } from "common/utils/message";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const FormWrapper = styled.div`
  padding: 40px 60px;
  display: flex;
  justify-content: center;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormItemInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const InputTitle = styled.div`
  color: #005754;
  font-family: "Poppins";
  font-size: 14px;
  font-weight: 500;
`;

const StyledInputNumber = styled(InputNumber)`
  outline: none;
  width: 100%;
  border-radius: 4px;
  border: 1px solid rgba(0, 87, 84, 0.5);
  background: linear-gradient(180deg, #fff 0%, #f5f5f5 100%);
  color: #2e2e2e;
  font-family: "Poppins";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 40px;
  @media screen and (max-width: 1279px) {
    font-size: 12px;
  }
`;

const TitleHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 10px;
  color: #005754;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
  padding-bottom: 10px;
`;

const BackImage = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
`;

const MainContainer = styled.div`
  padding: 20px 40px;
  width: 100%;
`;

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 40px;
  row-gap: 20px;
`;

const AddSCMPO = ()=>{
    const { authConfig } = useAuthContext();

    const navigate = useNavigate();

    const handleGoBack = () => {
        navigate("/scm-po");
     };

  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      ledgerId : undefined,
      materialCode : "",
      materialName : "",
      poNumber : "",
      referenceNumber : "",
      units : "",
      insurance : "",
      freight : "",
      casNumber : "",
      shipment : "",
      currency : undefined,
      payment : "",
      discount : "",
      quantity : "",
      rate : "",
      packingSize : "",
      note : ""
    }
  });

  const { mutate: submitGoodsMutate, isPending } = useMutation({
    mutationFn: (body) =>
      api(
        {
          url: APIConstants.addPurchaseOrder,
          method: "POST",
          body
        },
        authConfig
      ),
    onSuccess: () => {
      showMessage(
        "Goods Submitted Successfully",
        MESSAGE_TYPE.successTranslate     
      );
      navigate("/scm-po");
      reset();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data ||
        error?.message ||
        "Something went wrong";

      showMessage(errorMessage, MESSAGE_TYPE.errorTranslate);
    }
  });

  const { data : ledgers = [] } = useQuery({
          queryKey : ["ledgers"],
          queryFn : () =>
              api({
                  url : APIConstants.getActiveDebtorLedgers
              },
              authConfig
          )
      });
  
    const vendorNameOptions = ledgers?.map((item) => {
      const label = (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{item?.partyName}</span>

          {item?.partyName && item?.partyCode && (
            <span
              style={{
                fontWeight: "bold",
                margin: "0 6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              •
            </span>
          )}

          <span>{item?.partyCode}</span>
        </div>
        {item?.city && (
          <span style={{ fontSize: "12px", color: "#666" }}>
            {item.city}
          </span>
        )}
        
      </div>
    );
      return({
      value: item?.id, 
      label
      })
    })


  const onSubmit = (data) => {


    const poData = {
      ledgerId: data.ledgerId,
      materialCode : data.materialCode,
      materialName : data.materialName,
      poNumber: data.poNumber,
      referenceNumber : data.referenceNumber,
      units : data.units,
      insurance : data.insurance,
      freight : data.freight,
      casNumber : data.casNumber,
      shipment : data.shipment,
      currency : data.currency,
      payment : data.payment,
      discount: data.discount === "" ? 0 : data.discount,
      quantity : data.quantity,
      rate : data.rate,
      packingSize : data.packingSize,
      note : data.note
    };

    submitGoodsMutate(poData);
  };

  const options = [
    {value : "USD" , label : "USD"},
    {value : "INR" , label : "INR"},
    {value : "EUR" , label : "EUR"}
  ]
   return(
    <MainContainer>
        <TitleHeaderContainer>
          <BackImage src={backArrow} onClick={handleGoBack} alt="back" />
          <div>All Purchase Orders {">"} Add Purchase Order</div>
        </TitleHeaderContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
            <FormWrapper>
                <FormContainer>
                    <h1>Add Purchase Order</h1>
                    <TwoColumnRow>
                      <FormItemInner>
                      <InputTitle>Vendor Name *</InputTitle>
                      <Controller
                        name="ledgerId"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Select
                            showSearch
                            placeholder="Select Vendor Name"
                            options={vendorNameOptions}
                            optionFilterProp="label"
                            value={field.value}
                            onChange={(value) => {
                              field.onChange(value);
                            }}
                            allowClear
                          />
                        )}
                      />
                      </FormItemInner>
                      <FormItemInner>
                        <InputTitle>PO Number *</InputTitle>
                        <Controller
                          name="poNumber"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => <StyledInput {...field} placeholder="po number"/>}
                        />
                      </FormItemInner>
                    </TwoColumnRow>
                    <TwoColumnRow>
                      <FormItemInner>
                        <InputTitle>Material Name *</InputTitle>
                        <Controller
                          name="materialName"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => <StyledInput {...field} placeholder="material name"/>}
                        />
                      </FormItemInner>
                      <FormItemInner>
                        <InputTitle>Material Code *</InputTitle>
                        <Controller
                          name="materialCode"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => <StyledInput {...field} placeholder="material code"/>}
                        />
                      </FormItemInner>
                    </TwoColumnRow>
                    <TwoColumnRow>
                    <FormItemInner>
                      <InputTitle>Cas Number *</InputTitle>
                      <Controller
                        name="casNumber"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => <StyledInput {...field} placeholder="cas number"/>}
                      />
                    </FormItemInner>
                    <FormItemInner>
                      <InputTitle>Reference Number </InputTitle>
                      <Controller
                        name="referenceNumber"
                        control={control}
                        render={({ field }) => <StyledInput {...field} placeholder="reference number"/>}
                      />
                    </FormItemInner>
                    </TwoColumnRow>
                    <TwoColumnRow>
                      <FormItemInner>
                        <InputTitle>Units *</InputTitle>
                        <Controller
                          name="units"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => <StyledInput {...field} placeholder="units"/>}
                        />
                      </FormItemInner>
                      <FormItemInner>
                        <InputTitle>Currency *</InputTitle>
                        <Controller
                          name="currency"
                          control={control}
                          rules={{ required: true }}
                        render={({ field }) => (
                          <Select
                          style={{ width: "100%" }}
                            placeholder="Select currency"
                            {...field}
                            options={options}
                          />
                        )}
                      />
                    </FormItemInner>
                    </TwoColumnRow>
                    <TwoColumnRow>
                      <FormItemInner>
                        <InputTitle>Quantity *</InputTitle>
                        <Controller
                          name="quantity"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <StyledInputNumber {...field} placeholder="Quantity" min={0}/>
                          )}
                        />
                      </FormItemInner>
                      <FormItemInner>
                        <InputTitle>Rate *</InputTitle>
                        <Controller
                          name="rate"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <StyledInputNumber {...field} placeholder="rate"/>
                          )}
                        />
                      </FormItemInner>
                    </TwoColumnRow>
                    <TwoColumnRow>
                      <FormItemInner>
                        <InputTitle>Insurance </InputTitle>
                        <Controller
                          name="insurance"
                          control={control}

                          render={({ field }) => <StyledInput {...field} placeholder="insurance"/>}
                        />
                      </FormItemInner>
                      <FormItemInner>
                        <InputTitle>Freight </InputTitle>
                        <Controller
                          name="freight"
                          control={control}

                          render={({ field }) => <StyledInput {...field} placeholder="freight"/>}
                        />
                      </FormItemInner>
                    </TwoColumnRow>
                    <TwoColumnRow>

                      <FormItemInner>
                        <InputTitle>Shipment </InputTitle>
                        <Controller
                          name="shipment"
                          control={control}

                          render={({ field }) => <StyledInput {...field} placeholder="shipment"/>}
                        />
                      </FormItemInner>
                      <FormItemInner>
                        <InputTitle>Payment Terms</InputTitle>
                        <Controller
                          name="payment"
                          control={control}

                          render={({ field }) => <StyledInput {...field} placeholder="payment"/>}
                        />
                      </FormItemInner>
                    </TwoColumnRow>
                    <TwoColumnRow>    
                      <FormItemInner>
                        <InputTitle>Discount (%) </InputTitle>
                        <Controller
                          name="discount"
                          control={control}
                          render={({ field }) => <StyledInputNumber {...field} placeholder="discount"/>}
                        />
                      </FormItemInner>
                      <FormItemInner>
                        <InputTitle>Packing Size</InputTitle>
                        <Controller
                            name="packingSize"
                            control={control}
                            render={({ field }) => <StyledInput {...field} placeholder="packing size" />}
                        />
                      </FormItemInner>
                    </TwoColumnRow>
                    <FormItemInner>
                        <InputTitle>Note</InputTitle>
                        <Controller
                            name="note"
                            control={control}
                            render={({ field })=> <Input.TextArea {...field} placeholder="add note" rows={5}/>}
                        />
                    </FormItemInner>

                    <div style={{ textAlign: "center", marginTop: 24 }}>
                    <StyledButton
                        type="primary"
                        htmlType="submit"
                        disabled={isPending}
                    >
                        Add PO
                    </StyledButton>
                    </div>
                </FormContainer>
            </FormWrapper>
        </form>
    </MainContainer>
  )
};
export default AddSCMPO;  